import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Handshake, Check, X as XIcon, Plus, Banknote, Mail, Pencil,
  ChevronLeft, Copy, Link2, TrendingUp, Users, DollarSign,
  BarChart2, ExternalLink, ChevronDown, ChevronUp, Calendar,
  RefreshCw, CheckSquare, Square, Trash2, AlertTriangle, Shield, Download,
  FileText,
} from "lucide-react";
import { Card, Btn, Input, Field } from "@/components/admin/ui";
import { API } from "@/config";
import { fmtErr } from "@/context/AuthProvider";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const pct = (n) => `${Number(n || 0).toFixed(1)}%`;
const INPUT_CLS = "w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg focus:outline-none focus:border-[#FFD166] text-sm";

/* ── W-9 / Tax Documentation component ── */
const W9_STATUSES = [
  { value: "missing",          label: "Missing",          cls: "text-red-300 bg-red-900/20" },
  { value: "uploaded",         label: "Uploaded",         cls: "text-amber-300 bg-amber-900/20" },
  { value: "needs_correction", label: "Needs Correction", cls: "text-orange-300 bg-orange-900/20" },
  { value: "reviewed",         label: "Reviewed",         cls: "text-emerald-300 bg-emerald-900/20" },
];
const W9StatusBadge = ({ status }) => {
  const s = W9_STATUSES.find((x) => x.value === status) || W9_STATUSES[0];
  return <span className={`px-2 py-0.5 rounded text-xs font-mono ${s.cls}`}>{s.label}</span>;
};

const W9Section = ({ partnerId, partner, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [legalName, setLegalName] = useState(partner.legal_name || "");
  const [taxClass, setTaxClass] = useState(partner.tax_classification || "");
  const [ssnLast4, setSsnLast4] = useState(partner.ssn_last4 || "");
  const [w9Received, setW9Received] = useState(!!partner.w9_received);
  const [w9ReceivedAt, setW9ReceivedAt] = useState(partner.w9_received_at?.slice(0,10) || "");
  const [notes, setNotes] = useState(partner.w9_notes || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [w9Status, setW9Status] = useState(partner.w9_status || "missing");
  const [statusNote, setStatusNote] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/partners/${partnerId}/w9`, {
        legal_name: legalName, tax_classification: taxClass, ssn_last4: ssnLast4,
        w9_received: w9Received, w9_received_at: w9ReceivedAt || null, w9_notes: notes,
      });
      toast.success("W-9 info saved");
      setEditing(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are accepted for W-9 documents");
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/partners/${partnerId}/w9-upload-file`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`W-9 uploaded · ${res.data.filename} (${(res.data.size / 1024).toFixed(0)} KB)`);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadW9 = async (fileId) => {
    try {
      const url = fileId
        ? `${API}/admin/partners/${partnerId}/w9-download?file_id=${fileId}`
        : `${API}/admin/partners/${partnerId}/w9-download`;
      const res = await axios.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = partner.w9_file_doc?.original_name || `w9_${partnerId}.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const updateStatus = async () => {
    setSavingStatus(true);
    try {
      await axios.patch(`${API}/admin/partners/${partnerId}/w9-status`, { status: w9Status, note: statusNote });
      toast.success(`W-9 status updated to ${w9Status}`);
      setStatusNote("");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Status update failed");
    } finally { setSavingStatus(false); }
  };

  const fileDoc = partner.w9_file_doc;
  const history = partner.w9_history || [];
  const auditLog = partner.w9_audit_log || [];

  return (
    <Card className="p-4 mb-5" data-testid="partner-w9-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-orange-300" />
          <span className="font-serif text-lg">W-9 &amp; Tax Documentation</span>
          <W9StatusBadge status={partner.w9_status || "missing"} />
        </div>
        <Btn variant="ghost" onClick={() => setEditing(!editing)}><Pencil className="w-3 h-3" /> {editing ? "Cancel" : "Edit"}</Btn>
      </div>

      {/* W-9 file upload / download strip */}
      <div className="flex items-center gap-3 mb-3 p-2 bg-black/20 rounded-lg border border-[#FFD166]/10">
        {fileDoc ? (
          <>
            <FileText className="w-4 h-4 text-emerald-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-sm text-[#F6F1E4] truncate block">{fileDoc.original_name}</span>
              <span className="text-xs text-[#B9C8D5]/60">
                Uploaded {new Date(fileDoc.uploaded_at).toLocaleDateString()} by {fileDoc.uploaded_by}
                {fileDoc.size > 0 && ` · ${(fileDoc.size / 1024).toFixed(0)} KB`}
              </span>
            </div>
            <Btn variant="ghost" className="shrink-0 text-xs" onClick={() => downloadW9()}>
              <Download className="w-3 h-3" /> Download
            </Btn>
          </>
        ) : (
          <span className="text-sm text-amber-300 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> No W-9 PDF on file
          </span>
        )}
        <label className={`shrink-0 cursor-pointer text-xs px-3 py-1.5 rounded-md border transition ${uploading ? "opacity-50 pointer-events-none" : ""} border-[#FFD166]/30 text-[#FFD166] hover:bg-[#FFD166]/10`}>
          <input ref={fileRef} type="file" accept=".pdf,application/pdf" className="sr-only" onChange={uploadFile} disabled={uploading} />
          {uploading ? "Uploading…" : fileDoc ? "Replace PDF" : "Upload PDF"}
        </label>
      </div>

      {/* W-9 status management */}
      <div className="flex items-center gap-2 mb-3">
        <select
          className={`${INPUT_CLS} w-40`}
          value={w9Status}
          onChange={(e) => setW9Status(e.target.value)}
          data-testid="w9-status-select"
        >
          {W9_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <input
          className={`${INPUT_CLS} flex-1`}
          placeholder="Optional status note…"
          value={statusNote}
          onChange={(e) => setStatusNote(e.target.value)}
        />
        <Btn disabled={savingStatus} onClick={updateStatus} className="shrink-0">
          {savingStatus ? "Saving…" : "Update Status"}
        </Btn>
      </div>

      {!editing ? (
        <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
          <div className="flex justify-between border-b border-[#FFD166]/5 py-1">
            <span className="text-[#B9C8D5]">W-9 received</span>
            <span className={`font-medium ${w9Received ? "text-emerald-300" : "text-amber-300"}`}>
              {w9Received ? `Yes · ${w9ReceivedAt || "date not recorded"}` : "Not received"}
            </span>
          </div>
          <div className="flex justify-between border-b border-[#FFD166]/5 py-1">
            <span className="text-[#B9C8D5]">Reviewed</span>
            <span className="text-[#F6F1E4]">
              {partner.w9_reviewed_at
                ? `${new Date(partner.w9_reviewed_at).toLocaleDateString()} by ${partner.w9_reviewed_by || "admin"}`
                : <span className="text-[#B9C8D5]/60 italic">Not reviewed</span>}
            </span>
          </div>
          <div className="flex justify-between border-b border-[#FFD166]/5 py-1">
            <span className="text-[#B9C8D5]">Legal name</span>
            <span className="text-[#F6F1E4]">{legalName || <span className="text-amber-300 italic">Not on file</span>}</span>
          </div>
          <div className="flex justify-between border-b border-[#FFD166]/5 py-1">
            <span className="text-[#B9C8D5]">Tax classification</span>
            <span className="text-[#F6F1E4]">{taxClass || <span className="text-[#B9C8D5]/60 italic">Not set</span>}</span>
          </div>
          <div className="flex justify-between border-b border-[#FFD166]/5 py-1">
            <span className="text-[#B9C8D5]">SSN/EIN (last 4)</span>
            <span className="font-mono text-[#F6F1E4]">{ssnLast4 ? `···· ${ssnLast4}` : <span className="text-[#B9C8D5]/60 italic">Not set</span>}</span>
          </div>
          {notes && <div className="col-span-2 py-1 text-xs text-[#B9C8D5] italic">{notes}</div>}
          {!w9Received && (
            <p className="col-span-2 text-xs text-amber-300/80 mt-1">
              W-9 not on file. Payments over $2,000/year may require 1099 filing.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-[#B9C8D5]/70 italic">Do not store full SSNs/EINs — only last 4 digits for 1099 matching.</p>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs">
              <span className="text-[#B9C8D5] block mb-1">Legal name (as on W-9)</span>
              <input className={INPUT_CLS} value={legalName} onChange={(e) => setLegalName(e.target.value)} placeholder="Full legal name" />
            </label>
            <label className="text-xs">
              <span className="text-[#B9C8D5] block mb-1">Tax classification</span>
              <select className={INPUT_CLS} value={taxClass} onChange={(e) => setTaxClass(e.target.value)}>
                <option value="">Select…</option>
                <option value="individual">Individual / Sole Proprietor</option>
                <option value="llc_single">LLC (single member)</option>
                <option value="llc_partnership">LLC (partnership)</option>
                <option value="partnership">Partnership</option>
                <option value="s_corp">S Corporation</option>
                <option value="c_corp">C Corporation</option>
                <option value="trust">Trust/Estate</option>
              </select>
            </label>
            <label className="text-xs">
              <span className="text-[#B9C8D5] block mb-1">SSN/EIN last 4 digits only</span>
              <input className={INPUT_CLS} maxLength={4} value={ssnLast4} onChange={(e) => setSsnLast4(e.target.value.replace(/\D/g,""))} placeholder="e.g. 1234" />
            </label>
            <label className="text-xs">
              <span className="text-[#B9C8D5] block mb-1">W-9 received date</span>
              <input type="date" className={INPUT_CLS} value={w9ReceivedAt} onChange={(e) => setW9ReceivedAt(e.target.value)} />
            </label>
            <label className="text-xs flex items-center gap-2 col-span-2 cursor-pointer">
              <input type="checkbox" checked={w9Received} onChange={(e) => setW9Received(e.target.checked)} className="w-4 h-4" />
              <span className="text-[#B9C8D5]">W-9 received and on file</span>
            </label>
            <label className="text-xs col-span-2">
              <span className="text-[#B9C8D5] block mb-1">Notes</span>
              <input className={INPUT_CLS} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes about tax docs" />
            </label>
          </div>
          <div className="flex gap-2">
            <Btn onClick={save} disabled={saving}>{saving ? "Saving…" : "Save W-9 info"}</Btn>
            <Btn variant="ghost" onClick={() => setEditing(false)}>Cancel</Btn>
          </div>
        </div>
      )}

      {/* Version history */}
      {(history.length > 0 || auditLog.length > 0) && (
        <div className="mt-3 border-t border-[#FFD166]/10 pt-3">
          <button
            className="text-xs text-[#B9C8D5]/70 hover:text-[#FFD166] flex items-center gap-1"
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {showHistory ? "Hide" : "Show"} document history ({history.length} version{history.length !== 1 ? "s" : ""}, {auditLog.length} audit entries)
          </button>
          {showHistory && (
            <div className="mt-2 space-y-2">
              {history.length > 0 && (
                <div>
                  <div className="text-xs text-[#B9C8D5]/60 mb-1">Previous versions</div>
                  {history.filter(Boolean).map((doc, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-white/5">
                      <span className="text-[#B9C8D5]">{doc.original_name} · {new Date(doc.uploaded_at).toLocaleDateString()} · {doc.uploaded_by}</span>
                      <Btn variant="ghost" className="text-xs" onClick={() => downloadW9(doc.file_id)}>
                        <Download className="w-3 h-3" />
                      </Btn>
                    </div>
                  ))}
                </div>
              )}
              {auditLog.length > 0 && (
                <div>
                  <div className="text-xs text-[#B9C8D5]/60 mb-1">Status audit log</div>
                  {auditLog.slice().reverse().map((entry, i) => (
                    <div key={i} className="text-xs py-1 border-b border-white/5 text-[#B9C8D5]/70">
                      {entry.action} · {new Date(entry.at).toLocaleString()} · {entry.by}
                      {entry.note && <span className="italic ml-1">— {entry.note}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

const STATUS_CLS = {
  pending: "bg-[#F4A93A]/15 text-[#F4A93A]",
  approved: "bg-emerald-500/15 text-emerald-300",
  inactive: "bg-white/10 text-[#B9C8D5]",
  denied: "bg-red-500/15 text-red-300",
};
const COMM_STATUS_CLS = {
  projected: "bg-blue-500/15 text-blue-300",
  earned: "bg-emerald-500/15 text-emerald-300",
  paid: "bg-[#FFD166]/15 text-[#FFD166]",
};

const Sel = (p) => (
  <select {...p} className={`w-full bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg ${p.className || ""}`} />
);

const CopyBtn = ({ text, label = "Copy" }) => {
  const [copied, setCopied] = useState(false);
  const click = () => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(true);
    toast.success("Copied!");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button type="button" onClick={click}
      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-[#FFD166]/30 hover:border-[#FFD166]/60 text-[#FFD166]"
      data-testid="copy-btn">
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied!" : label}
    </button>
  );
};

const DATE_PRESETS = [
  { label: "All time", from: null, to: null },
  { label: "This month", from: () => new Date().toISOString().slice(0, 7) + "-01", to: null },
  { label: "Last month", from: () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7) + "-01"; }, to: () => { const d = new Date(); d.setDate(0); return d.toISOString().slice(0, 10); } },
  { label: "This year", from: () => new Date().getFullYear() + "-01-01", to: null },
  { label: "Custom", from: null, to: null, custom: true },
];

const DateFilter = ({ dateFrom, dateTo, onDateFrom, onDateTo }) => {
  const [preset, setPreset] = useState(0);
  const apply = (idx) => {
    const p = DATE_PRESETS[idx];
    if (p.custom) return;
    setPreset(idx);
    onDateFrom(typeof p.from === "function" ? p.from() : p.from);
    onDateTo(typeof p.to === "function" ? p.to() : p.to);
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="affiliate-date-filter">
      {DATE_PRESETS.map((p, i) => !p.custom && (
        <button key={p.label} type="button" onClick={() => apply(i)}
          className={`px-3 py-1 rounded-full border text-xs ${preset === i ? "border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166]" : "border-[#B9C8D5]/30 text-[#B9C8D5]"}`}>
          {p.label}
        </button>
      ))}
      <div className="flex items-center gap-1">
        <input type="date" value={dateFrom || ""} onChange={e => { setPreset(4); onDateFrom(e.target.value || null); }}
          className="bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] text-xs px-2 py-1 rounded" data-testid="filter-from" />
        <span className="text-[#B9C8D5] text-xs">–</span>
        <input type="date" value={dateTo || ""} onChange={e => { setPreset(4); onDateTo(e.target.value || null); }}
          className="bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] text-xs px-2 py-1 rounded" data-testid="filter-to" />
      </div>
    </div>
  );
};

// ── Approve Modal ────────────────────────────────────────────────────────────
const ApproveModal = ({ partner, onClose, onDone }) => {
  const [f, setF] = useState({
    code: partner.code || `${(partner.business_name || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10)}10`,
    customer_discount_type: partner.customer_discount_type || "percent",
    customer_discount_percent: partner.customer_discount_percent ?? 10,
    customer_discount_flat: partner.customer_discount_flat ?? 0,
    max_customer_discount: partner.max_customer_discount ?? "",
    commission_percent: partner.commission_percent ?? 10,
    start_date: partner.start_date || "",
    review_date: partner.review_date || "",
    end_date: partner.end_date || "",
  });
  const [busy, setBusy] = useState(false);

  const go = async () => {
    setBusy(true);
    try {
      const payload = {
        ...f,
        customer_discount_percent: Number(f.customer_discount_percent),
        customer_discount_flat: Number(f.customer_discount_flat),
        commission_percent: Number(f.commission_percent),
        max_customer_discount: f.max_customer_discount !== "" ? Number(f.max_customer_discount) : null,
      };
      await axios.post(`${API}/admin/partners/${partner.id}/approve`, payload);
      toast.success("Partner approved — invite email sent");
      onDone();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const isFlat = f.customer_discount_type === "flat";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="partner-approve-modal">
      <Card className="p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h3 className="font-serif text-2xl mb-4">Approve {partner.business_name}</h3>
        <div className="space-y-4">
          <Field label="Referral / promo code">
            <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} data-testid="approve-code" />
          </Field>

          <div className="border border-[#FFD166]/20 rounded-lg p-4 space-y-3">
            <p className="text-xs uppercase tracking-widest text-[#FFD166]">Customer Discount</p>
            <Field label="Discount type">
              <Sel value={f.customer_discount_type} onChange={(e) => setF({ ...f, customer_discount_type: e.target.value })} data-testid="approve-discount-type">
                <option value="percent">Percentage (%)</option>
                <option value="flat">Flat dollar amount ($)</option>
              </Sel>
            </Field>
            {isFlat ? (
              <Field label="Flat discount amount ($)">
                <Input type="number" min="0" step="0.01" value={f.customer_discount_flat}
                  onChange={(e) => setF({ ...f, customer_discount_flat: e.target.value })} data-testid="approve-discount-flat" />
              </Field>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Discount %">
                  <Input type="number" min="0" max="100" value={f.customer_discount_percent}
                    onChange={(e) => setF({ ...f, customer_discount_percent: e.target.value })} data-testid="approve-discount" />
                </Field>
                <Field label="Max discount ($, optional)">
                  <Input type="number" min="0" step="0.01" placeholder="No cap"
                    value={f.max_customer_discount} onChange={(e) => setF({ ...f, max_customer_discount: e.target.value })} data-testid="approve-max-discount" />
                </Field>
              </div>
            )}
          </div>

          <div className="border border-emerald-500/20 rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-emerald-300 mb-3">Affiliate Commission</p>
            <Field label="Commission % (applied to commissionable subtotal after customer discount)">
              <Input type="number" min="0" max="100" step="0.1" value={f.commission_percent}
                onChange={(e) => setF({ ...f, commission_percent: e.target.value })} data-testid="approve-commission" />
            </Field>
            <p className="text-xs text-[#B9C8D5] mt-2">Commission is always calculated AFTER the customer discount and BEFORE tax.</p>
          </div>

          <div className="border border-[#B9C8D5]/10 rounded-lg p-4 space-y-3">
            <p className="text-xs uppercase tracking-widest text-[#B9C8D5]">Partnership Dates (optional)</p>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Start date"><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></Field>
              <Field label="Review date"><Input type="date" value={f.review_date} onChange={(e) => setF({ ...f, review_date: e.target.value })} /></Field>
              <Field label="End date"><Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></Field>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={go} disabled={busy} data-testid="approve-confirm">
            <Check className="w-3.5 h-3.5" /> {busy ? "Approving…" : "Approve & send invite"}
          </Btn>
        </div>
      </Card>
    </div>
  );
};

// ── Payout Modal (with order selection) ─────────────────────────────────────
const PayoutModal = ({ partner, onClose, onDone }) => {
  const [unpaid, setUnpaid] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [f, setF] = useState({ paid_on: new Date().toISOString().slice(0, 10), reference: "", notes: "", payment_method: "paypal" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/partners/${partner.id}/unpaid-commissions`)
      .then(r => {
        setUnpaid(r.data.items || []);
        setSelected(new Set((r.data.items || []).map(i => i.reservation_id)));
      })
      .catch(() => toast.error("Couldn't load unpaid commissions"));
  }, [partner.id]);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => setSelected(selected.size === unpaid.length ? new Set() : new Set(unpaid.map(i => i.reservation_id)));

  const selectedItems = unpaid.filter(i => selected.has(i.reservation_id));
  const totalSelected = selectedItems.reduce((sum, i) => sum + i.commission, 0);

  const go = async () => {
    if (!f.paid_on) { toast.error("Payout date required"); return; }
    if (totalSelected <= 0) { toast.error("Select at least one commission to include"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/partners/${partner.id}/payouts`, {
        ...f, amount: Math.round(totalSelected * 100) / 100,
        order_ids: Array.from(selected),
      });
      toast.success("Payout recorded");
      onDone();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="partner-payout-modal">
      <Card className="p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h3 className="font-serif text-2xl mb-1">Record Payout</h3>
        <p className="text-sm text-[#B9C8D5] mb-4">{partner.business_name} · PayPal: {partner.paypal_email}</p>

        {unpaid.length === 0 ? (
          <p className="text-sm text-[#B9C8D5] py-4">No unpaid commissions to include.</p>
        ) : (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-widest text-[#B9C8D5]">Select commissions to include</p>
              <button type="button" onClick={toggleAll} className="text-xs text-[#FFD166] hover:underline"
                data-testid="payout-select-all">
                {selected.size === unpaid.length ? "Deselect all" : "Select all"}
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {unpaid.map(item => (
                <div key={item.reservation_id}
                  onClick={() => toggle(item.reservation_id)}
                  className={`flex items-center justify-between p-2 rounded cursor-pointer border ${selected.has(item.reservation_id) ? "border-emerald-500/40 bg-emerald-500/5" : "border-[#B9C8D5]/10"}`}
                  data-testid={`payout-item-${item.reservation_id}`}>
                  <div className="flex items-center gap-2">
                    {selected.has(item.reservation_id)
                      ? <CheckSquare className="w-4 h-4 text-emerald-300" />
                      : <Square className="w-4 h-4 text-[#B9C8D5]/50" />}
                    <span className="font-mono text-xs text-[#FFD166]">{item.order_number}</span>
                    <span className="text-xs text-[#B9C8D5]">{item.delivery_date} → {item.pickup_date}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="text-[#B9C8D5]">Eligible: {money(item.eligible_subtotal)}</span>
                    <span className="text-emerald-300 font-semibold">{money(item.commission)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-2 text-right text-sm">
              Selected: <span className="text-[#FFD166] font-semibold">{money(totalSelected)}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Payout date"><Input type="date" value={f.paid_on} onChange={(e) => setF({ ...f, paid_on: e.target.value })} data-testid="payout-date" /></Field>
          <Field label="Payment method">
            <Sel value={f.payment_method} onChange={(e) => setF({ ...f, payment_method: e.target.value })} data-testid="payout-method">
              <option value="paypal">PayPal</option>
              <option value="venmo">Venmo</option>
              <option value="check">Check</option>
              <option value="wire">Wire Transfer</option>
              <option value="other">Other</option>
            </Sel>
          </Field>
          <Field label="Reference / transaction ID">
            <Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="payout-reference" />
          </Field>
          <Field label="Notes">
            <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="payout-notes" />
          </Field>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={go} disabled={busy || totalSelected <= 0} data-testid="payout-confirm">
            <Banknote className="w-3.5 h-3.5" /> {busy ? "Saving…" : `Record payout · ${money(totalSelected)}`}
          </Btn>
        </div>
      </Card>
    </div>
  );
};

// ── Edit Form ────────────────────────────────────────────────────────────────
const EditForm = ({ partner, onSaved }) => {
  const [f, setF] = useState({
    business_name: partner.business_name || "",
    contact_name: partner.contact_name || "",
    email: partner.email || "",
    phone: partner.phone || "",
    paypal_email: partner.paypal_email || "",
    website: partner.website || "",
    code: partner.code || "",
    customer_discount_type: partner.customer_discount_type || "percent",
    customer_discount_percent: partner.customer_discount_percent ?? 10,
    customer_discount_flat: partner.customer_discount_flat ?? 0,
    max_customer_discount: partner.max_customer_discount ?? "",
    commission_percent: partner.commission_percent ?? 10,
    status: partner.status || "pending",
    start_date: partner.start_date || "",
    review_date: partner.review_date || "",
    end_date: partner.end_date || "",
    notes: partner.notes || "",
    admin_notes: partner.admin_notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    const body = {
      ...f,
      code: f.code || undefined,
      customer_discount_percent: Number(f.customer_discount_percent),
      customer_discount_flat: Number(f.customer_discount_flat),
      commission_percent: Number(f.commission_percent),
      max_customer_discount: f.max_customer_discount !== "" ? Number(f.max_customer_discount) : null,
    };
    try {
      await axios.patch(`${API}/admin/partners/${partner.id}`, body);
      toast.success("Partner updated");
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const isFlat = f.customer_discount_type === "flat";

  return (
    <Card className="p-5" data-testid="partner-edit-form">
      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <Field label="Business name"><Input value={f.business_name} onChange={set("business_name")} data-testid="pe-business" /></Field>
        <Field label="Contact name"><Input value={f.contact_name} onChange={set("contact_name")} data-testid="pe-contact" /></Field>
        <Field label="Email"><Input value={f.email} onChange={set("email")} data-testid="pe-email" /></Field>
        <Field label="Phone"><Input value={f.phone} onChange={set("phone")} data-testid="pe-phone" /></Field>
        <Field label="PayPal email"><Input value={f.paypal_email} onChange={set("paypal_email")} data-testid="pe-paypal" /></Field>
        <Field label="Website"><Input value={f.website} onChange={set("website")} data-testid="pe-website" /></Field>
        <Field label="Status">
          <Sel value={f.status} onChange={set("status")} data-testid="pe-status">
            {["pending", "approved", "inactive", "denied"].map(s => <option key={s} value={s}>{s}</option>)}
          </Sel>
        </Field>
        <Field label="Promo code"><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} data-testid="pe-code" /></Field>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div className="border border-[#FFD166]/20 rounded-lg p-4 space-y-3">
          <p className="text-xs uppercase tracking-widest text-[#FFD166]">Customer Discount</p>
          <Field label="Discount type">
            <Sel value={f.customer_discount_type} onChange={set("customer_discount_type")} data-testid="pe-discount-type">
              <option value="percent">Percentage (%)</option>
              <option value="flat">Flat dollar amount ($)</option>
            </Sel>
          </Field>
          {isFlat ? (
            <Field label="Flat amount ($)"><Input type="number" min="0" step="0.01" value={f.customer_discount_flat} onChange={set("customer_discount_flat")} data-testid="pe-discount-flat" /></Field>
          ) : (
            <>
              <Field label="Discount %"><Input type="number" min="0" max="100" value={f.customer_discount_percent} onChange={set("customer_discount_percent")} data-testid="pe-discount" /></Field>
              <Field label="Max discount ($, blank = no cap)"><Input type="number" min="0" step="0.01" placeholder="No cap" value={f.max_customer_discount} onChange={set("max_customer_discount")} data-testid="pe-max-discount" /></Field>
            </>
          )}
        </div>
        <div className="border border-emerald-500/20 rounded-lg p-4 space-y-3">
          <p className="text-xs uppercase tracking-widest text-emerald-300">Affiliate Commission</p>
          <Field label="Commission % (post-discount)"><Input type="number" min="0" max="100" step="0.1" value={f.commission_percent} onChange={set("commission_percent")} data-testid="pe-commission" /></Field>
          <p className="text-xs text-[#B9C8D5]">Commission is always calculated after the customer discount, before tax.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <Field label="Partnership start"><Input type="date" value={f.start_date} onChange={set("start_date")} /></Field>
        <Field label="Review date"><Input type="date" value={f.review_date} onChange={set("review_date")} /></Field>
        <Field label="Partnership end"><Input type="date" value={f.end_date} onChange={set("end_date")} /></Field>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Partner notes (visible to partner)"><Input value={f.notes} onChange={set("notes")} data-testid="pe-notes" /></Field>
        <Field label="Admin notes (private — not visible to affiliate)">
          <Input value={f.admin_notes} onChange={set("admin_notes")} data-testid="pe-admin-notes" />
        </Field>
      </div>

      <div className="flex justify-end mt-4">
        <Btn onClick={save} disabled={busy} data-testid="pe-save">{busy ? "Saving…" : "Save changes"}</Btn>
      </div>
    </Card>
  );
};

// ── Reservation Drawer (inline mini view) ───────────────────────────────────
const ReservationMiniDrawer = ({ reservationId, onClose }) => {
  const [res, setRes] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/reservations/${reservationId}`, { withCredentials: true })
      .then(r => setRes(r.data))
      .catch(() => toast.error("Couldn't load reservation"));
  }, [reservationId]);

  if (!res) return (
    <div className="fixed inset-0 z-60 bg-black/80 flex items-center justify-center" data-testid="res-mini-drawer">
      <Card className="p-6 max-w-2xl w-full"><p className="text-[#B9C8D5]">Loading reservation…</p></Card>
    </div>
  );

  const partnerDisc = Number(res.partner_discount || 0);
  const commNet = Number(res.partner_commissionable_subtotal || 0);
  const comm = Number(res.partner_commission || 0);
  const enchanted = Number(res.partner_enchanted_share || 0);
  const eligible = Number(res.partner_eligible_subtotal || 0);

  return (
    <div className="fixed inset-0 z-60 bg-black/80 flex items-center justify-center p-4" data-testid="res-mini-drawer">
      <Card className="p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl">Order #{res.id?.slice(0, 8).toUpperCase()}</h3>
          <button type="button" onClick={onClose} className="text-[#B9C8D5] hover:text-white"><XIcon className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#B9C8D5]">Customer</p>
            <p>{res.customer_name}</p>
            <p className="text-[#B9C8D5] text-xs">{res.customer_email}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#B9C8D5]">Rental period</p>
            <p>{res.delivery_date} → {res.pickup_date}</p>
            <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${STATUS_CLS[res.status] || "bg-white/10 text-[#B9C8D5]"}`}>{res.status}</span>
          </div>
        </div>

        {res.items?.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] uppercase tracking-widest text-[#B9C8D5] mb-2">Items</p>
            {res.items.map((item, i) => (
              <div key={i} className="flex justify-between text-sm py-1 border-b border-[#FFD166]/10">
                <span>{item.product_name} × {item.quantity}</span>
                <span className="text-[#B9C8D5]">{money(item.subtotal)}</span>
              </div>
            ))}
          </div>
        )}

        {res.partner_id && (
          <div className="bg-[#1A2E40] rounded-lg p-4 mb-4 text-sm">
            <p className="text-[10px] uppercase tracking-widest text-[#FFD166] mb-3">Affiliate Attribution</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex justify-between"><span className="text-[#B9C8D5]">Eligible subtotal</span><span>{money(eligible)}</span></div>
              <div className="flex justify-between"><span className="text-[#B9C8D5]">Customer discount</span><span className="text-amber-300">-{money(partnerDisc)}</span></div>
              <div className="flex justify-between font-semibold border-t border-[#FFD166]/20 pt-2"><span>Commissionable</span><span>{money(commNet)}</span></div>
              <div className="flex justify-between text-[#FFD166]"><span>Affiliate commission</span><span>-{money(comm)}</span></div>
              <div className="flex justify-between text-emerald-300"><span>Enchanted share</span><span>{money(enchanted)}</span></div>
            </div>
            <p className="text-xs text-[#B9C8D5] mt-2">
              Code: <span className="font-mono text-[#FFD166]">{res.partner_code}</span>
              {res.partner_commission_paid && <span className="ml-3 text-emerald-300">Commission paid</span>}
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex justify-between"><span className="text-[#B9C8D5]">Subtotal</span><span>{money(res.subtotal)}</span></div>
          {Number(res.partner_discount || 0) > 0 && (
            <div className="flex justify-between"><span className="text-[#B9C8D5]">Affiliate discount</span><span className="text-amber-300">-{money(res.partner_discount)}</span></div>
          )}
          <div className="flex justify-between"><span className="text-[#B9C8D5]">Tax</span><span>{money(res.tax)}</span></div>
          <div className="flex justify-between border-t border-[#FFD166]/20 pt-2 font-semibold">
            <span>Total</span><span className="text-[#FFD166]">{money(res.total)}</span>
          </div>
          <div className="flex justify-between text-xs"><span className="text-[#B9C8D5]">Payment status</span>
            <span className={res.payment_status === "paid" ? "text-emerald-300" : "text-amber-300"}>{res.payment_status}</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ── Partner Detail ────────────────────────────────────────────────────────────
const PartnerDetail = ({ id, onBack, onOpenReservation }) => {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const [payout, setPayout] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [openResId, setOpenResId] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    return axios.get(`${API}/admin/partners/${id}?${params}`)
      .then(r => setD(r.data))
      .catch(() => toast.error("Couldn't load partner"));
  }, [id, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  if (!d) return <p className="text-[#B9C8D5]">Loading…</p>;
  const { partner, totals, projected, analytics, bookings = [], payouts = [] } = d;

  const origin = window.location.origin;
  const referralUrl = partner.referral_token ? `${origin}/?ref=${partner.referral_token}` : null;

  const realized = bookings.filter(b => !b.is_future && b.status !== "cancelled");
  const upcoming = bookings.filter(b => b.is_future && b.status !== "cancelled");
  const cancelled = bookings.filter(b => b.status === "cancelled");

  const resend = async () => {
    try {
      await axios.post(`${API}/admin/partners/${id}/resend-invite`);
      toast.success("Login link sent");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="partner-detail">
      {openResId && <ReservationMiniDrawer reservationId={openResId} onClose={() => setOpenResId(null)} />}

      <button type="button" onClick={onBack} className="text-xs text-[#B9C8D5] hover:text-[#FFD166] inline-flex items-center gap-1 mb-3" data-testid="partner-detail-back">
        <ChevronLeft className="w-3 h-3" /> All affiliates
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h1 className="font-serif text-4xl">{partner.business_name}</h1>
          <p className="text-sm text-[#B9C8D5] mt-1">{partner.contact_name} · {partner.email} · {partner.phone}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${STATUS_CLS[partner.status]}`} data-testid="partner-detail-status">{partner.status}</span>
            {partner.code && <span className="font-mono text-[#FFD166] text-sm" data-testid="partner-detail-code">Code: {partner.code}</span>}
            {partner.start_date && <span className="text-xs text-[#B9C8D5]">Since {partner.start_date}</span>}
            {partner.review_date && <span className="text-xs text-amber-300">Review: {partner.review_date}</span>}
            {partner.end_date && <span className="text-xs text-red-300">Ends: {partner.end_date}</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Btn variant="ghost" onClick={() => setEdit(!edit)} data-testid="partner-detail-edit"><Pencil className="w-3.5 h-3.5" /> Edit</Btn>
          {partner.user_id && <Btn variant="ghost" onClick={resend} data-testid="partner-detail-resend"><Mail className="w-3.5 h-3.5" /> Resend login</Btn>}
          <Btn onClick={() => setPayout(true)} data-testid="partner-detail-mark-paid"><Banknote className="w-3.5 h-3.5" /> Record payout</Btn>
        </div>
      </div>

      {/* Referral link */}
      {referralUrl && (
        <Card className="p-4 mb-4 flex flex-wrap items-center gap-4" data-testid="partner-referral-card">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#B9C8D5]">Referral link</p>
            <p className="font-mono text-xs text-[#FFD166] mt-1 break-all">{referralUrl}</p>
          </div>
          <CopyBtn text={referralUrl} label="Copy link" />
          <div className="ml-auto text-xs text-[#B9C8D5]">
            {analytics?.total_clicks || 0} clicks · {analytics?.unique_sessions || 0} unique · {analytics?.conversion_rate || 0}% conversion
          </div>
        </Card>
      )}

      {edit && (
        <div className="mb-5">
          <EditForm partner={partner} onSaved={() => { setEdit(false); load(); }} />
        </div>
      )}

      {/* Date filter */}
      <div className="mb-4">
        <DateFilter dateFrom={dateFrom} dateTo={dateTo} onDateFrom={setDateFrom} onDateTo={setDateTo} />
      </div>

      {/* Financials */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          ["Qualifying bookings", totals.bookings, "pt-bookings"],
          ["Eligible sales", money(totals.eligible_sales), "pt-sales"],
          ["Customer discounts", money(totals.customer_discounts), "pt-discounts"],
          ["Net commissionable", money(totals.net_commissionable), "pt-commissionable"],
        ].map(([l, v, t]) => (
          <Card key={l} className="p-3" data-testid={t}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#B9C8D5]">{l}</p>
            <p className="font-serif text-xl mt-1">{v}</p>
          </Card>
        ))}
        {[
          ["Enchanted share", money(totals.enchanted_share), "pt-enchanted", "text-emerald-300"],
          ["Commission earned", money(totals.commission_earned), "pt-earned", "text-[#FFD166]"],
          ["Commission paid", money(totals.commission_paid), "pt-paid", ""],
          ["Balance owed", money(totals.balance_owed), "pt-balance", "text-amber-300"],
        ].map(([l, v, t, cls]) => (
          <Card key={l} className="p-3" data-testid={t}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#B9C8D5]">{l}</p>
            <p className={`font-serif text-xl mt-1 ${cls}`}>{v}</p>
          </Card>
        ))}
      </div>

      {/* W-9 / Tax Documentation */}
      <W9Section partnerId={id} partner={partner} onSaved={load} />

      {/* UPCOMING / PROJECTED (admin-only) */}
      <Card className="p-4 mb-5" data-testid="partner-upcoming-section">
        <button type="button" onClick={() => setShowUpcoming(!showUpcoming)}
          className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-300" />
            <span className="font-serif text-lg">Upcoming / Projected</span>
            {projected?.bookings > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[10px] bg-blue-500/15 text-blue-300 uppercase">
                {projected.bookings} booking{projected.bookings !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          {showUpcoming ? <ChevronUp className="w-4 h-4 text-[#B9C8D5]" /> : <ChevronDown className="w-4 h-4 text-[#B9C8D5]" />}
        </button>

        {showUpcoming && (
          <div className="mt-4">
            <div className="grid grid-cols-4 gap-3 mb-4">
              {[
                ["Projected bookings", projected?.bookings || 0],
                ["Projected sales", money(projected?.eligible_sales)],
                ["Projected discounts", money(projected?.customer_discounts)],
                ["Projected commission", money(projected?.commission)],
              ].map(([l, v]) => (
                <div key={l} className="bg-blue-500/5 rounded-lg p-3 border border-blue-500/20">
                  <p className="text-[10px] uppercase tracking-widest text-blue-300">{l}</p>
                  <p className="font-semibold text-lg mt-1">{v}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-blue-300 mb-3">
              PROJECTED — not included in earned/owed totals above. These bookings are hidden from the affiliate until they start.
            </p>
            {upcoming.length > 0 ? (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[#B9C8D5] border-b border-[#FFD166]/15">
                  <th className="py-2 pr-3">Order</th><th className="py-2 pr-3">Customer</th>
                  <th className="py-2 pr-3">Rental start</th><th className="py-2 pr-3">Eligible</th>
                  <th className="py-2 pr-3">Discount</th><th className="py-2 pr-3">Proj. commission</th>
                  <th className="py-2">Status</th>
                </tr></thead>
                <tbody>
                  {upcoming.map(b => (
                    <tr key={b.reservation_id} className="border-b border-[#FFD166]/5" data-testid={`upcoming-${b.reservation_id}`}>
                      <td className="py-2 pr-3">
                        <button type="button" onClick={() => setOpenResId(b.reservation_id)}
                          className="font-mono text-blue-300 hover:underline" data-testid={`open-res-${b.reservation_id}`}>
                          {b.order_number} <ExternalLink className="w-3 h-3 inline" />
                        </button>
                      </td>
                      <td className="py-2 pr-3 text-[#B9C8D5]">{b.customer_name}</td>
                      <td className="py-2 pr-3">{b.delivery_date}</td>
                      <td className="py-2 pr-3">{money(b.eligible_subtotal)}</td>
                      <td className="py-2 pr-3 text-amber-300">{money(b.customer_discount)}</td>
                      <td className="py-2 pr-3 text-blue-300">{money(b.commission_original)}</td>
                      <td className="py-2 capitalize text-xs">{b.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-[#B9C8D5]">No upcoming bookings.</p>
            )}
          </div>
        )}
      </Card>

      {/* Qualified bookings */}
      <Card className="p-4 mb-5 overflow-x-auto" data-testid="partner-bookings-card">
        <h3 className="font-serif text-xl mb-3">Qualifying Bookings ({realized.length})</h3>
        {realized.length === 0 ? <p className="text-sm text-[#B9C8D5]">No qualifying bookings yet.</p> : (
          <table className="w-full text-sm" data-testid="partner-bookings-table">
            <thead><tr className="text-left text-[#B9C8D5] border-b border-[#FFD166]/15">
              <th className="py-2 pr-3">Order</th><th className="py-2 pr-3">Rental dates</th>
              <th className="py-2 pr-3">Eligible</th><th className="py-2 pr-3">Discount</th>
              <th className="py-2 pr-3">Commissionable</th><th className="py-2 pr-3">Enchanted share</th>
              <th className="py-2 pr-3">Commission</th><th className="py-2 pr-3">Comm status</th>
              <th className="py-2">Booking status</th>
            </tr></thead>
            <tbody>
              {realized.map(b => (
                <tr key={b.reservation_id} className="border-b border-[#FFD166]/5" data-testid={`pb-${b.reservation_id}`}>
                  <td className="py-2 pr-3">
                    <button type="button" onClick={() => setOpenResId(b.reservation_id)}
                      className="font-mono text-[#FFD166] hover:underline" data-testid={`open-res-${b.reservation_id}`}>
                      {b.order_number} <ExternalLink className="w-3 h-3 inline opacity-50" />
                    </button>
                  </td>
                  <td className="py-2 pr-3 text-[#B9C8D5] text-xs">{b.delivery_date} → {b.pickup_date}</td>
                  <td className="py-2 pr-3">{money(b.eligible_subtotal)}</td>
                  <td className="py-2 pr-3 text-amber-300">-{money(b.customer_discount)}</td>
                  <td className="py-2 pr-3">{money(b.commissionable_subtotal)}</td>
                  <td className="py-2 pr-3 text-emerald-300">{money(b.enchanted_share)}</td>
                  <td className="py-2 pr-3 text-[#FFD166]">{money(b.commission)}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase ${COMM_STATUS_CLS[b.commission_status] || "bg-white/10 text-[#B9C8D5]"}`}>
                      {b.commission_status}
                    </span>
                  </td>
                  <td className="py-2 capitalize text-xs">{b.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Cancelled orders */}
      {cancelled.length > 0 && (
        <Card className="p-4 mb-5 overflow-x-auto">
          <h3 className="font-serif text-xl mb-3 text-red-300">Cancelled Orders ({cancelled.length})</h3>
          <table className="w-full text-sm opacity-70">
            <thead><tr className="text-left text-[#B9C8D5] border-b border-red-500/15">
              <th className="py-2 pr-3">Order</th><th className="py-2 pr-3">Rental dates</th>
              <th className="py-2 pr-3">Eligible</th><th className="py-2 pr-3">Commission</th>
            </tr></thead>
            <tbody>
              {cancelled.map(b => (
                <tr key={b.reservation_id} className="border-b border-red-500/10">
                  <td className="py-2 pr-3">
                    <button type="button" onClick={() => setOpenResId(b.reservation_id)}
                      className="font-mono text-red-300 hover:underline">{b.order_number}</button>
                  </td>
                  <td className="py-2 pr-3 text-[#B9C8D5] text-xs">{b.delivery_date} → {b.pickup_date}</td>
                  <td className="py-2 pr-3">{money(b.eligible_subtotal)}</td>
                  <td className="py-2 pr-3 text-red-300">{money(b.commission)} (voided)</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Payouts */}
      <Card className="p-4 overflow-x-auto">
        <h3 className="font-serif text-xl mb-3">Payout history</h3>
        {payouts.length === 0 ? <p className="text-sm text-[#B9C8D5]">No payouts recorded.</p> : (
          <table className="w-full text-sm" data-testid="partner-payouts-table">
            <thead><tr className="text-left text-[#B9C8D5] border-b border-[#FFD166]/15">
              <th className="py-2 pr-3">Paid on</th><th className="py-2 pr-3">Amount</th>
              <th className="py-2 pr-3">Method</th><th className="py-2 pr-3">Orders</th>
              <th className="py-2 pr-3">Reference</th><th className="py-2">Recorded by</th>
            </tr></thead>
            <tbody>
              {payouts.map(p => (
                <tr key={p.id} className="border-b border-[#FFD166]/5">
                  <td className="py-2 pr-3">{p.paid_on}</td>
                  <td className="py-2 pr-3 text-[#FFD166]">{money(p.amount)}</td>
                  <td className="py-2 pr-3 text-[#B9C8D5] capitalize">{p.payment_method || "paypal"}</td>
                  <td className="py-2 pr-3 text-xs text-[#B9C8D5]">{p.order_ids?.length || "—"} orders</td>
                  <td className="py-2 pr-3 font-mono text-xs">{p.reference || "—"}</td>
                  <td className="py-2 text-[#B9C8D5] text-xs">{p.created_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {payout && <PayoutModal partner={partner} onClose={() => setPayout(false)} onDone={() => { setPayout(false); load(); }} />}
    </div>
  );
};

// ── Main Partners list ────────────────────────────────────────────────────────
export const Partners = () => {
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [approving, setApproving] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [adding, setAdding] = useState(false);
  const [nf, setNf] = useState({ business_name: "", contact_name: "", email: "", phone: "", paypal_email: "", partner_type: "hotel" });

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/admin/partners`),
      axios.get(`${API}/admin/partners/summary`),
    ]).then(([pl, sl]) => {
      setList(pl.data.partners || []);
      setSummary(sl.data);
    }).catch(() => toast.error("Couldn't load partners"));
  }, []);

  useEffect(() => { load(); }, [load]);

  const deny = async (p) => {
    const reason = window.prompt("Reason (optional)") ?? "";
    try {
      await axios.post(`${API}/admin/partners/${p.id}/deny`, { reason });
      toast.success("Denied");
      load();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    }
  };
  const setStatus = async (p, status) => {
    try { await axios.patch(`${API}/admin/partners/${p.id}`, { status }); load(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  const create = async () => {
    try {
      await axios.post(`${API}/admin/partners`, nf);
      toast.success("Partner added (pending) — approve to issue a code & send invite");
      setAdding(false);
      setNf({ business_name: "", contact_name: "", email: "", phone: "", paypal_email: "", partner_type: "hotel" });
      load();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    }
  };

  if (selected) return <PartnerDetail id={selected} onBack={() => { setSelected(null); load(); }} />;

  const shown = filter === "all" ? list : list.filter(p => p.status === filter);
  const pending = list.filter(p => p.status === "pending").length;

  return (
    <div data-testid="admin-partners">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl flex items-center gap-2">
            <Handshake className="w-7 h-7 text-[#FFD166]" /> Affiliates / Partners
          </h1>
          <p className="text-[#B9C8D5] text-sm mt-1 max-w-2xl">
            Hotels, resorts, property managers, agents and creators. Each approved affiliate gets a promo code
            and a unique referral link. Both attribute the booking.
            {pending > 0 && <span className="text-[#F4A93A] ml-2">{pending} application{pending > 1 ? "s" : ""} awaiting review.</span>}
          </p>
        </div>
        <Btn onClick={() => setAdding(!adding)} data-testid="partner-add-btn"><Plus className="w-3.5 h-3.5" /> Add affiliate</Btn>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-6">
          {[
            ["Active", summary.active_partners, "text-emerald-300", "aff-active"],
            ["Clicks", summary.total_clicks, "", "aff-clicks"],
            ["Sessions", summary.unique_sessions, "", "aff-sessions"],
            ["Bookings", summary.bookings, "", "aff-bookings"],
            ["Cust. discounts", money(summary.customer_discounts), "text-amber-300", "aff-discounts"],
            ["Commission earned", money(summary.commission_earned), "text-[#FFD166]", "aff-earned"],
            ["Balance owed", money(summary.balance_owed), "text-amber-300", "aff-owed"],
            ["Enchanted share", money(summary.enchanted_share), "text-emerald-300", "aff-enchanted"],
          ].map(([l, v, cls, t]) => (
            <Card key={l} className="p-2.5" data-testid={t}>
              <p className="text-[9px] uppercase tracking-widest text-[#B9C8D5]">{l}</p>
              <p className={`font-semibold text-sm mt-0.5 ${cls}`}>{v}</p>
            </Card>
          ))}
        </div>
      )}

      {adding && (
        <Card className="p-5 mb-5" data-testid="partner-add-form">
          <div className="grid md:grid-cols-3 gap-3">
            <Field label="Business name"><Input value={nf.business_name} onChange={e => setNf({ ...nf, business_name: e.target.value })} data-testid="np-business" /></Field>
            <Field label="Contact name"><Input value={nf.contact_name} onChange={e => setNf({ ...nf, contact_name: e.target.value })} data-testid="np-contact" /></Field>
            <Field label="Email"><Input value={nf.email} onChange={e => setNf({ ...nf, email: e.target.value })} data-testid="np-email" /></Field>
            <Field label="Phone"><Input value={nf.phone} onChange={e => setNf({ ...nf, phone: e.target.value })} data-testid="np-phone" /></Field>
            <Field label="PayPal email"><Input value={nf.paypal_email} onChange={e => setNf({ ...nf, paypal_email: e.target.value })} data-testid="np-paypal" /></Field>
            <Field label="Type">
              <Sel value={nf.partner_type} onChange={e => setNf({ ...nf, partner_type: e.target.value })} data-testid="np-type">
                {["hotel", "resort", "vacation_rental", "property_manager", "travel_agent", "concierge", "influencer", "local_business", "other"].map(t => (
                  <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
                ))}
              </Sel>
            </Field>
          </div>
          <div className="flex justify-end mt-3">
            <Btn onClick={create} data-testid="np-save">Create affiliate</Btn>
          </div>
        </Card>
      )}

      <div className="flex gap-2 mb-4 flex-wrap items-center">
        {["all", "pending", "approved", "inactive", "denied"].map(f => (
          <button key={f} type="button" onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full border text-xs capitalize ${filter === f ? "border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166]" : "border-[#B9C8D5]/30 text-[#B9C8D5]"}`}
            data-testid={`partner-filter-${f}`}>{f}</button>
        ))}
        <a href={`${API}/admin/partners/export-1099?year=2026&threshold=2000`}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-orange-500/30 text-orange-300 text-xs hover:bg-orange-500/10"
          data-testid="export-1099-btn">
          <Download className="w-3 h-3" /> 1099 Export (2026)
        </a>
      </div>

      <Card className="p-4 overflow-x-auto">
        {shown.length === 0 ? (
          <p className="text-sm text-[#B9C8D5] py-4 text-center" data-testid="partners-empty">No affiliates here yet.</p>
        ) : (
          <table className="w-full text-sm" data-testid="partners-table">
            <thead>
              <tr className="text-left text-[#B9C8D5] border-b border-[#FFD166]/15">
                <th className="py-2 pr-3">Affiliate</th>
                <th className="py-2 pr-3">Code / Link</th>
                <th className="py-2 pr-3">Discount</th>
                <th className="py-2 pr-3">Commission</th>
                <th className="py-2 pr-3">Bookings</th>
                <th className="py-2 pr-3">Earned</th>
                <th className="py-2 pr-3">Owed</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {shown.map(p => {
                const discLabel = p.customer_discount_type === "flat"
                  ? `$${Number(p.customer_discount_flat || 0).toFixed(0)} flat`
                  : `${Number(p.customer_discount_percent || 0).toFixed(0)}%${p.max_customer_discount ? ` (max $${Number(p.max_customer_discount).toFixed(0)})` : ""}`;
                return (
                  <tr key={p.id} className="border-b border-[#FFD166]/5 hover:bg-black/25" data-testid={`partner-row-${p.id}`}>
                    <td className="py-2 pr-3">
                      <button type="button" onClick={() => setSelected(p.id)}
                        className="text-[#FFD166] hover:underline text-left font-medium" data-testid={`partner-open-${p.id}`}>
                        {p.business_name}
                      </button>
                      <div className="text-[11px] text-[#B9C8D5] capitalize">{(p.partner_type || "").replace(/_/g, " ")}</div>
                    </td>
                    <td className="py-2 pr-3">
                      <span className="font-mono text-[#FFD166] text-xs">{p.code || "—"}</span>
                    </td>
                    <td className="py-2 pr-3 text-xs">{discLabel}</td>
                    <td className="py-2 pr-3 text-xs">{Number(p.commission_percent || 0).toFixed(0)}%</td>
                    <td className="py-2 pr-3">{p.totals?.bookings || 0}</td>
                    <td className="py-2 pr-3 text-[#FFD166]">{money(p.totals?.commission_earned)}</td>
                    <td className="py-2 pr-3 text-amber-300" data-testid={`partner-owed-${p.id}`}>{money(p.totals?.balance_owed)}</td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${STATUS_CLS[p.status]}`}
                        data-testid={`partner-status-${p.id}`}>{p.status}</span>
                    </td>
                    <td className="py-2 whitespace-nowrap text-xs">
                      {p.status === "pending" && (
                        <>
                          <button type="button" onClick={() => setApproving(p)} className="text-emerald-300 hover:underline mr-3" data-testid={`partner-approve-${p.id}`}>
                            <Check className="w-3 h-3 inline" /> Approve
                          </button>
                          <button type="button" onClick={() => deny(p)} className="text-red-300 hover:underline" data-testid={`partner-deny-${p.id}`}>
                            <XIcon className="w-3 h-3 inline" /> Deny
                          </button>
                        </>
                      )}
                      {p.status === "approved" && (
                        <button type="button" onClick={() => setStatus(p, "inactive")} className="text-[#B9C8D5] hover:text-[#FFD166]" data-testid={`partner-deactivate-${p.id}`}>Deactivate</button>
                      )}
                      {p.status === "inactive" && (
                        <button type="button" onClick={() => setStatus(p, "approved")} className="text-emerald-300 hover:underline" data-testid={`partner-reactivate-${p.id}`}>Reactivate</button>
                      )}
                      {p.status === "denied" && (
                        <button type="button" onClick={() => setApproving(p)} className="text-emerald-300 hover:underline" data-testid={`partner-approve-${p.id}`}>Approve anyway</button>
                      )}
                      <button type="button" onClick={() => setDeleteTarget(p)} className="ml-3 text-red-400/60 hover:text-red-400 hover:underline" data-testid={`partner-delete-${p.id}`}>
                        <Trash2 className="w-3 h-3 inline" /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {approving && (
        <ApproveModal partner={approving} onClose={() => setApproving(null)} onDone={() => { setApproving(null); load(); }} />
      )}
      {deleteTarget && (
        <DeletePartnerModal partner={deleteTarget} onClose={() => setDeleteTarget(null)} onDone={() => { setDeleteTarget(null); load(); }} />
      )}
    </div>
  );
};

/* ---------- Delete Partner Modal ---------- */
const DeletePartnerModal = ({ partner: p, onClose, onDone }) => {
  const [impact, setImpact] = useState(null);
  const [confirmed, setConfirmed] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/partners/${p.id}/delete-impact`).then((r) => setImpact(r.data)).catch(() => setImpact({}));
  }, [p.id]);

  const handleDelete = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/partners/${p.id}`);
      toast.success(`${p.business_name} permanently deleted.`);
      onDone();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  const nameMatches = confirmed.trim().toLowerCase() === (p.business_name || "").trim().toLowerCase();

  return (
    <div className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="delete-partner-modal">
      <div className="glass max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h3 className="font-serif text-xl text-red-400">Permanently delete affiliate</h3>
            <p className="text-xs text-[#B9C8D5]">{p.business_name}</p>
          </div>
        </div>

        <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/5 mb-4 text-xs space-y-1 text-red-200/80">
          <p><strong>This action is permanent and cannot be undone.</strong></p>
          <p>The affiliate's account, login access, and payout records will be permanently removed.</p>
          <p>Existing reservation attribution records will be preserved for historical reporting.</p>
        </div>

        {impact ? (
          <div className="rounded-lg border border-[#FFD166]/15 bg-black/30 p-3 mb-4 text-xs space-y-1">
            <p className="text-[#B9C8D5] text-[10px] uppercase tracking-wider mb-1">Impact Summary</p>
            <div className="flex justify-between"><span className="text-[#B9C8D5]">Attributed orders</span><span className={impact.order_count > 0 ? "text-amber-300 font-medium" : "text-[#F6F1E4]"}>{impact.order_count}</span></div>
            <div className="flex justify-between"><span className="text-[#B9C8D5]">Payout records</span><span className={impact.payout_count > 0 ? "text-amber-300 font-medium" : "text-[#F6F1E4]"}>{impact.payout_count}</span></div>
            {impact.payout_total > 0 && <div className="flex justify-between"><span className="text-[#B9C8D5]">Total payouts recorded</span><span className="text-amber-300 font-medium">${Number(impact.payout_total).toFixed(2)}</span></div>}
          </div>
        ) : (
          <div className="h-16 flex items-center justify-center text-[#B9C8D5] text-xs mb-4">Loading impact…</div>
        )}

        <div className="mb-4">
          <label className="block text-xs text-[#B9C8D5] mb-1">
            Type the affiliate name to confirm: <strong className="text-[#FFD166]">{p.business_name}</strong>
          </label>
          <Input value={confirmed} onChange={(e) => setConfirmed(e.target.value)} placeholder={p.business_name} data-testid="delete-confirm-input" />
        </div>

        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn
            onClick={handleDelete}
            disabled={busy || !nameMatches}
            className="bg-red-500/20 border-red-500/40 text-red-300 hover:bg-red-500/30 disabled:opacity-40"
            data-testid="delete-confirm-btn"
          >
            <Trash2 className="w-4 h-4" /> {busy ? "Deleting…" : "Delete permanently"}
          </Btn>
        </div>
      </div>
    </div>
  );
};

export default Partners;
